import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import {
  matchPath,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { PageLoaderContainer } from '@/hocs/PageLoaderContainer';

import { MainNavigation } from '@components/layout/navigation/MainNavigation';
import { OmaSATOFooter } from '@components/OmaSATOFooter';
import { DialogProvider } from '@providers/DialogProvider';
import { ROUTES } from '@routes/routes.constants';
import { trimLeadingHash } from '@utils/link.utils';
import { scrollToElementWithRetries } from '@utils/scroll.utils';
import { LOGIN_ROUTES } from '@views/login/login.constants';
import { OngoingParkingRentingBanner } from '@views/parking/OngoingParkingRentingBanner';

export const MainLayout = observer(() => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  // ScrollRestoration restores the saved scroll position of a pathname also when navigating to it via a link.
  // Scroll to the top instead on link navigation (PUSH). Back/forward (POP) keeps the restored position, and
  // replacing the URL (REPLACE) is used for in-page changes, e.g. picking a message subtopic.
  //
  // Runs after ScrollRestoration, since child layout effects run first. Only depends on pathname, so that
  // changing query params keeps the scroll position.
  //
  useLayoutEffect(() => {
    if (navigationType === 'PUSH' && !hash) window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // scroll to an anchor
  useEffect(() => {
    if (!hash) return;

    // Site might be slow to load. Attempt to scroll to the element multiple times.
    scrollToElementWithRetries(trimLeadingHash(hash));
  }, [hash]);

  const hideNavigation = useMemo(
    () =>
      [
        ...Object.values(LOGIN_ROUTES),
        ROUTES.MARKETING_PERMISSION,
        ROUTES.CHANGE_PASSWORD,
      ].some((route) => matchPath(pathname, route)),
    [pathname]
  );

  return (
    <PageLoaderContainer className="flex min-h-screen w-full flex-col">
      {/* DialogProvider uses react router links and thus has to be inside the router */}
      <DialogProvider>
        <ScrollRestoration getKey={(location) => location.pathname} />

        {!hideNavigation && <MainNavigation />}

        <main className="flex w-full grow flex-col">
          <Outlet />

          <OngoingParkingRentingBanner />
        </main>

        {!hideNavigation && <OmaSATOFooter />}
      </DialogProvider>
    </PageLoaderContainer>
  );
});
